import { cx, useTranslations } from "@jugl-web/utils";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import OTPInput from "react-otp-input";
import { usePrevious } from "react-use";
import { CountDownTimer } from "./components/CountDownTimer";

export type OTPVerificationFormParams = {
  title?: ReactNode | string;
  subTitle?: ReactNode | string;
  otp?: string;
  className?: string;
  onChange?: (otp: string) => void;
  onSubmit?: (otp: string) => void;
  onResend: () => void;
  error?: string;
};

export const OTPVerificationForm: React.FC<OTPVerificationFormParams> = ({
  title,
  subTitle,
  otp: initialOtp,
  className,
  onChange,
  onSubmit,
  onResend,
  error,
}) => {
  const { t } = useTranslations();
  const [otp, setOtp] = useState(initialOtp || "");
  const previousOpt = usePrevious(otp);
  const isOTPValid = useMemo(() => otp.length === 6, [otp]);
  const [timerActive, setTimerActive] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(0);

  useEffect(() => {
    if (!isOTPValid || previousOpt === otp) {
      return;
    }
    onSubmit?.(otp);
  }, [isOTPValid, onSubmit, otp, previousOpt]);

  return (
    <div
      className={cx(
        "flex h-full w-full max-w-[450px] min-w-0 grow flex-col items-center px-0 py-4 sm:p-8",
        className
      )}
    >
      {(title || subTitle) && (
        <div className="flex flex-col items-center gap-3 pb-8 text-center sm:pb-[64px]">
          {title && (
            <span className="w-full text-[28px] leading-9 font-medium text-dark sm:w-3/4 sm:text-[32px] sm:leading-[48px]">
              {title}
            </span>
          )}
          {subTitle && (
            <span className="text-sm text-grey-900">{subTitle}</span>
          )}
        </div>
      )}
      <div className="flex w-full flex-col items-center">
        <OTPInput
          value={otp}
          onChange={(e) => {
            const value = e.replace(/[^0-9]/g, "");
            onChange?.(value);
            setOtp(value);
          }}
          containerStyle={{
            justifyContent: "center",
            gap: "10px",
            height: "56px",
          }}
          renderInput={(props, index) => (
            <div className="relative shrink-0">
              <input
                autoFocus={index === 0}
                {...props}
                aria-label={`Digit ${index + 1} of 6`}
                className="select-none focus:outline-none"
                onBlur={() => setFocusedIndex(null)}
                onClick={() => setFocusedIndex(index)}
              />
              <div
                className="absolute right-[11px] bottom-2 left-[11px] h-[2px] rounded-lg transition-opacity duration-200"
                style={{
                  backgroundColor: error ? "#F3CED2" : "#D9D9D9",
                  opacity: focusedIndex === index ? 1 : 0,
                }}
              />
            </div>
          )}
          inputType="tel"
          inputStyle={{
            color: "#1A2023",
            fontFamily: "Poppins, sans-serif",
            // react-otp-input defaults width to 1em; override with a box that
            // stays 48px on desktop and scales slightly on narrow viewports.
            width: "clamp(40px, 11vw, 48px)",
            height: "48px",
            background: error ? "#FAECEE" : "#F2F2F4",
            border: "none",
            borderRadius: "10px",
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: "500",
            textAlign: "center",
          }}
          numInputs={6}
        />
        {error && (
          <span className="pt-4 text-xs text-gradients-danger">{error}</span>
        )}
      </div>
      <div className="mt-auto flex flex-col items-center gap-1 pt-8 text-xs sm:pt-[64px]">
        <span className="text-grey-700">
          {t({
            id: "otp-verification-page.code-not-received-message",
            defaultMessage: "Didn't get code yet?",
          })}
        </span>
        {timerActive ? (
          <CountDownTimer
            onCountDownEnd={() => {
              setTimerActive(false);
            }}
          />
        ) : (
          <span
            className="cursor-pointer text-sm font-semibold text-primary"
            onClick={() => {
              setTimerActive(true);
              onResend();
            }}
          >
            {t({
              id: "otp-verification-page.tap-to-resend",
              defaultMessage: "Tap to Resend",
            })}
          </span>
        )}
      </div>
    </div>
  );
};
